import { formatDuration, formatPercentTenths, percentTenths } from './dashboardSummaryUtils';
import styles from './DashboardSummaryCards.module.scss';

function UptimeRatioCard({ usageData, errorData, loading = false }) {
  if (loading) {
    return (
      <div className={styles.summaryPanel}>
        <div className={styles.loadingMessage}>Loading run time ratio...</div>
      </div>
    );
  }

  // Error sessions are a subset of usage sessions (errors occur *during* usage),
  // so usage total_minutes already includes the error minutes. Total tracked time
  // is therefore the usage total alone — adding error minutes would double-count.
  const totalTrackedMinutes = usageData?.total_minutes || 0;
  const totalErrorMinutes = Math.min(errorData?.total_minutes || 0, totalTrackedMinutes);
  const totalUsageMinutes = totalTrackedMinutes - totalErrorMinutes;
  const usageRatio = totalTrackedMinutes > 0 ? totalUsageMinutes / totalTrackedMinutes : 0;
  // Downtime share is the remainder of the rounded run share, so the two add up to 100.0%
  const usagePct = formatPercentTenths(percentTenths(usageRatio));
  const errorPct = formatPercentTenths(
    totalTrackedMinutes > 0 ? 1000 - percentTenths(usageRatio) : 0
  );
  const donutRadius = 54;
  const donutCircumference = 2 * Math.PI * donutRadius;
  const usageStrokeOffset = donutCircumference * (1 - usageRatio);

  return (
    <div className={styles.summaryPanel}>
      <h3 className={styles.panelTitle}>
        Run Time Ratio
        <span className={styles.panelSubtitle}>
          {`Tracked total · ${formatDuration(totalTrackedMinutes)}`}
        </span>
      </h3>
      <p className={styles.chartSubtitle}>
        Compare run time and downtime across the same summary window as the calendars.
      </p>
      {totalTrackedMinutes > 0 ? (
        <div className={styles.donutLayout}>
          <div className={styles.donutChart}>
            <svg
              className={styles.donutSvg}
              viewBox="0 0 140 140"
              role="img"
              aria-label={`Run time ${usagePct}, downtime ${errorPct}`}
            >
              <circle cx="70" cy="70" r={donutRadius} className={styles.donutTrack} />
              <circle
                cx="70"
                cy="70"
                r={donutRadius}
                className={styles.donutUsage}
                strokeDasharray={donutCircumference}
                strokeDashoffset={usageStrokeOffset}
              />
            </svg>
            <div className={styles.donutCenter}>
              <span className={styles.donutValue}>{usagePct}</span>
              <span className={styles.donutLabel}>stability</span>
            </div>
          </div>
          <div className={styles.chartMetrics}>
            <div className={styles.metricItem}>
              <span className={styles.metricSwatch} style={{ backgroundColor: '#2f8f5b' }} />
              <div className={styles.metricText}>
                <span className={styles.metricLabel}>Run Time</span>
                <span className={styles.metricValue}>
                  {formatDuration(totalUsageMinutes)} · {usagePct}
                </span>
              </div>
            </div>
            <div className={styles.metricItem}>
              <span className={styles.metricSwatch} style={{ backgroundColor: '#d64545' }} />
              <div className={styles.metricText}>
                <span className={styles.metricLabel}>Downtime</span>
                <span className={styles.metricValue}>
                  {formatDuration(totalErrorMinutes)} · {errorPct}
                </span>
              </div>
            </div>
            <div className={styles.metricCallout}>
              {totalErrorMinutes > 0
                ? `${formatDuration(totalUsageMinutes)} run time for every ${formatDuration(totalErrorMinutes)} downtime`
                : 'No downtime recorded in this period'}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.noData}>No run time or downtime data</div>
      )}
    </div>
  );
}

export default UptimeRatioCard;
